import { axiosInstance } from '@/plugins/axios';
import { convertLutGeoMapsToGeoMaps } from '@/components/channels/Territories/TerritoriesUtils';
import errorTracker from '@/plugins/errorTracker';
import lodash from 'lodash';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import campaignService from '@/services/campaignService';
import { leadTypeService } from '@/services/leadType';
import { calcOverridesForSchedule, calcDailyCapsFromWeeklyCap } from '@/services/daycaps.service';
import request from '@/utils/request';
import { showErrorToast } from '@/utils/toast';

/**
 * Helper function to update a channel (non-campaign lead type)
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object} leadType - The lead type to update
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
async function updateChannel(dispatch, leadType) {
  const queryParams = leadType.estimateAmount ? `?estimateAmount=${leadType.estimateAmount}` : '';

  const response = await request.post(
    `/api/LeadType/edit/${leadType.serviceType}${queryParams}`,
    leadType,
    {
      errorMessage: `Failed to update ${leadType.serviceType.toLowerCase()} channel`,
    }
  );

  if (!response) {
    return false;
  }

  const saveScheduleResult = await dispatch('scheduler/saveSchedules', leadType.id, {
    root: true,
  });

  if (!saveScheduleResult) {
    return false;
  }

  if (leadType.serviceType.toLowerCase() === 'call') {
    const corpFilterSaveResult = await dispatch('callCorporateFilter/saveFilters', leadType.id, {
      root: true,
    });

    if (!corpFilterSaveResult) {
      return false;
    }
  }

  return true;
}

/**
 * Helper function to update a campaign
 * @param {Object} rootGetters - Vuex root getters
 * @param {Object} rootState - Vuex root state
 * @param {Object} leadType - The lead type to update
 * @returns {Promise} Promise that resolves to the response object
 */
async function updateCampaign(rootGetters, rootState, leadType) {
  const scheduleData = rootGetters['scheduler/marshalledScheduleData'](leadType.id);
  const filters = [...rootState.callCorporateFilter.filters];

  return request.post(
    '/api/campaigns/save',
    {
      clientQuoteType: leadType,
      schedule: scheduleData,
      filters,
    },
    {
      errorMessage: `Failed to update ${leadType.serviceType.toLowerCase()} campaign`,
    }
  );
}

/**
 * Helper function to create a channel (non-campaign lead type)
 * @param {Function} dispatch - Vuex dispatch function
 * @param {Object} leadType - The lead type to create
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
async function createChannel(dispatch, leadType) {
  const response = await request.post(`/api/leadtype/add/${leadType.serviceType}`, leadType, {
    errorMessage: `Failed to create ${leadType.serviceType.toLowerCase()} channel`,
  });

  if (!response) {
    return false;
  }

  await dispatch('scheduler/saveSchedules', response.data, {
    root: true,
  });

  if (leadType.serviceType.toLowerCase() === 'call') {
    await dispatch('callCorporateFilter/saveFilters', response.data, {
      root: true,
    });
  }

  return true;
}

/**
 * Helper function to create a campaign
 * @param {Object} rootGetters - Vuex root getters
 * @param {Object} rootState - Vuex root state
 * @param {Object} leadType - The lead type to create
 * @returns {Promise} Promise that resolves to the response object
 */
async function createCampaign(rootGetters, rootState, leadType) {
  const scheduleData = rootGetters['scheduler/marshalledScheduleData'](leadType.id);
  const filters = [...rootState.callCorporateFilter.filters];

  return request.post(
    '/api/campaigns/save',
    {
      clientQuoteType: leadType,
      schedule: scheduleData,
      filters,
    },
    {
      errorMessage: `Failed to create ${leadType.serviceType.toLowerCase()} campaign`,
    }
  );
}

export const DEFAULT_PACKAGE_ID = '00000000-0000-0000-0000-000000000000';
export const DEFAULT_MAX_SOLD = 4;

export const getInitialState = () => ({
  accountReps: [],
  availableLutQuoteTypes: [],
  cancelSavePackages: [],
  crmTypes: [],
  inactivePackage: null,
  isLoading: false,
  leadType: {
    acceptsBundle: false,
    acceptsCombo: false,
    answerRate: 0.0,
    backfill: true,
    bidFloor: null,
    billableRate: 0.0,
    clientGeoMaps: {
      removed: [],
      added: [],
    },
    clientId: null,
    createDate: null,
    crmDelivery: [],
    currentSubsidy: 0,
    currentPromoDiscount: 0,
    customName: null,
    dayCap: 0,
    dayCapSchedule: {
      sunday: -1,
      monday: -1,
      tuesday: -1,
      wednesday: -1,
      thursday: -1,
      friday: -1,
      saturday: -1,
      weekly: -1,
    },
    discount: 0,
    effectivePayoutRate: 0,
    effectiveValue: 0.0,
    emailDelivery: [],
    enableRecording: true,
    eprMultiplier: null,
    eprResetDate: new Date(),
    estimateAmount: null,
    estimateId: 0,
    estimatedMonthlyLeads: 0,
    expansionOptOut: false,
    filter: null,
    filterList: null,
    geoMaps: [],
    id: null,
    initialPackageId: DEFAULT_PACKAGE_ID,
    isLegAnchor: false,
    lastLeadDate: '0001-01-01T00:00:00',
    maxSold: 0,
    minutesBeforeNext: 0,
    missedCallPenalty: 0.0,
    missedCallRate: 0.0,
    modifier: 0.0,
    nextActiveDate: '0001-01-01T00:00:00',
    packageId: DEFAULT_PACKAGE_ID,
    payout: 0,
    payoutDiscount: 0,
    postFailureRate: 0,
    postTemplate: '',
    phoneNumbers: [],
    priceModifiers: [],
    productSourceTypeId: 0,
    productTypeId: 1,
    quoteMode: null,
    quoteType: 'Auto',
    quoteTypeId: null,
    quoteTypeIsLocked: true,
    rank: 0,
    schedule: [],
    secondaryTerritoryExcludedZipCodes: [],
    serviceType: 'Web',
    scrubRate: 0,
    sourcePackage: null,
    status: 'Active',
    subscriptionDescription: '',
    tierMixCap: 0,
    trumpFlag: 0,
    updateDate: null,
    upsellRep: '',
    upsellPromo: '',
    weekdayNetPayout: 0,
    weekendNetPayout: 0,
    subsidyProduct: null,
  },
  originalPackageId: '',
  packageBasePrice: 0,
  packages: [],
  stateSettingsOverrides: [],
  storedPriceModifiers: [],
  customPriceModifiers: [],
  temporaryPauseDuration: null,
  territoryDetails: {
    secondaryTerritoryZipCodes: [],
    territorySettings: [],
  },
  packageRestrictions: {
    maxSold: DEFAULT_MAX_SOLD,
  },
  originalDayCapOverrides: null,
});

export const moduleState = getInitialState();

export const getters = {
  filters: state => {
    if (state.leadType) {
      return state.leadType.filterList && state.leadType.filterList.split(', ');
    }
    return [];
  },
  hasOwnsHomeFilter: state => state.leadType?.filterList?.includes('AUTO_OWN_HOME'),
  hasCustomPackage: state => state.leadType.packageId === DEFAULT_PACKAGE_ID,
  loading: state => state.isLoading,
  getTerritories: state => state.leadType.geoMaps,
  selectedStates: state => [...new Set(state.leadType.geoMaps.map(x => x.stateAbbreviation))],
  getExcludedZips: state => state.leadType.secondaryTerritoryExcludedZipCodes,
  getExpansionOptOut: state => state.leadType.expansionOptOut,
  quoteType: state => state.leadType.quoteType,
  subsidyProduct: state => state.leadType.subsidyProduct,
  subsidyToolTipMessage: () =>
    'Applicable subsidies are based on tier, product, and the lead county as determined by State Farm.',
  subsidyByPercentToolTipMessage: () =>
    'Maximum applicable subsidies are based on product, level; Subsidy restrictions are by lead county and as determined by State Farm.',
  isWeb: state => state.leadType?.serviceType === 'Web',
  isCall: state => state.leadType?.serviceType === 'Call',
  availableLeadTypes: (state, getters, rootState, rootGetters) => {
    const clientType =
      rootState.client.client?.clientType || rootState.signup.signupInfo?.client?.clientType;
    const parentCompany =
      rootState.client.client?.parentCompany || rootState.signup.signupInfo?.client?.parentCompany;
    return leadTypeService.getAvailableQuoteTypes(
      rootState.auth.roles,
      clientType,
      parentCompany,
      state.leadType?.serviceType,
      rootGetters['featureFlagStore/medicareConsentEnabled']
    );
  },
  isCampaign: state => !!state.stateSettingsOverrides.length,
  crmTypesLookup: state =>
    (state?.crmTypes ?? []).reduce((a, c) => {
      a[c.domain.toLowerCase()] = c;
      return a;
    }, {}),
  selectedPackage: state => {
    const packageId = state.leadType?.packageId;
    return state.packages.find(p => p.id === packageId);
  },
  maxSold: state => state.leadType?.maxSold,
  isCustomMaxSold: state =>
    state.leadType?.maxSold !== (state.packageRestrictions?.maxSold ?? DEFAULT_MAX_SOLD),
  packageRestrictions: state => state.packageRestrictions,
};

export const mutations = {
  setLoading(state, value) {
    state.isLoading = value;
  },

  RESET_STATE(state) {
    const initialState = getInitialState();
    Object.keys(state).forEach(key => {
      state[key] = initialState[key];
    });
  },
  SET_LEAD_TYPE(state, newValue) {
    if (newValue.isNew) {
      newValue.id = uuidv4();
    }
    newValue.emailDelivery = newValue.emailDelivery || [];
    newValue.crmDelivery = newValue.crmDelivery || [];
    newValue.phoneNumbers = newValue.phoneNumbers || [];
    state.leadType = newValue;
  },
  SET_TERRITORY_DETAILS(state, newValue) {
    state.territoryDetails = newValue;
  },
  SET_DAY_CAP(state, { day, value }) {
    state.leadType.dayCapSchedule[day.toLowerCase()] = value;
    if (day.toLowerCase() === 'weekly') {
      state.leadType.weeklyCap = value;
    }
  },
  SET_DAY_CAPS(state, { dayCapSchedule, dayCapOverrides, clonedChannel }) {
    state.leadType.dayCapSchedule = dayCapSchedule;
    state.leadType.dayCapOverrides = state.originalDayCapOverrides != null ? dayCapOverrides : null;
    state.leadType.recalcDayCapOverrides = dayCapOverrides !== null && clonedChannel === null;
    state.leadType.capsClonedFrom = clonedChannel;
  },
  SET_CLIENT_GEOMAP(state, newValue) {
    state.leadType.clientGeoMaps.removed = newValue.removed;
    state.leadType.clientGeoMaps.added = newValue.added;
  },
  RESET_DAY_CAPS(state, resetValue) {
    const days = Object.keys(state.leadType.dayCapSchedule);
    for (const day of days) {
      if (day !== 'weekly') {
        // reset all to Monday cap value except weekly cap
        state.leadType.dayCapSchedule[day] = resetValue;
      }
    }
  },
  SET_CRM_TYPES(state, crmTypes) {
    state.crmTypes = crmTypes;
  },
  SET_CRM_DELIVERY(state, newValue) {
    state.leadType.crmDelivery = newValue;
  },
  SET_EMAIL_DELIVERY(state, emails) {
    state.leadType.emailDelivery = emails;
  },
  REMOVE_EMAIL_DELIVERY(state, index) {
    state.leadType.emailDelivery.splice(index, 1);
  },
  ADD_EMAIL_DELIVERY(state, newEmail) {
    state.leadType.emailDelivery.push(newEmail);
  },
  UPDATE_EMAIL_DELIVERY(state, { index, updatedEmail }) {
    state.leadType.emailDelivery[index] = updatedEmail;
  },
  RESET_EMAIL_DELIVERY(state) {
    if (!state.leadType.emailDelivery) {
      state.leadType.emailDelivery = [];
    } else {
      state.leadType.emailDelivery.splice(0, state.leadType.emailDelivery.length);
    }
  },
  REMOVE_CRM_DELIVERY(state, index) {
    state.leadType.crmDelivery.splice(index, 1);
  },
  ADD_CRM_DELIVERY(state, newCrm) {
    state.leadType.crmDelivery.push(newCrm);
  },
  UPDATE_CRM_DELIVERY(state, { index, updatedCrm }) {
    state.leadType.crmDelivery[index] = updatedCrm;
  },
  RESET_CRM_DELIVERY(state) {
    if (!state.leadType.crmDelivery) {
      state.leadType.crmDelivery = [];
    } else {
      state.leadType.crmDelivery.splice(0, state.leadType.crmDelivery.length);
    }
  },
  SET_PHONE_DELIVERY(state, phones) {
    state.leadType.phoneNumbers = phones;
  },
  REMOVE_PHONE_DELIVERY(state, index) {
    state.leadType.phoneNumbers.splice(index, 1);
  },
  ADD_PHONE_DELIVERY(state, newPhone) {
    state.leadType.phoneNumbers.push(newPhone);
  },
  UPDATE_PHONE_DELIVERY(state, { index, updatedPhone }) {
    state.leadType.phoneNumbers[index] = updatedPhone;
  },
  RESET_PHONE_DELIVERY(state) {
    if (!state.leadType.phoneNumbers) {
      state.leadType.phoneNumbers = [];
    } else {
      state.leadType.phoneNumbers.splice(0, state.leadType.phoneNumbers.length);
    }
  },
  SET_PACKAGE_ID(state, newValue) {
    state.leadType.packageId = newValue;
  },
  SET_ACCEPTS_COMBO(state, newValue) {
    state.leadType.acceptsCombo = newValue;
  },
  SET_SERVICE_TYPE(state, newValue) {
    state.leadType.serviceType = newValue;
    state.leadType.productTypeId = newValue === 'Web' ? 1 : 2;
  },
  SET_PRODUCT_TYPE_ID(state, newValue) {
    state.leadType.productTypeId = newValue;
    state.leadType.serviceType = newValue === 1 ? 'Web' : 'Call';
  },
  SET_QUOTE_TYPE(state, newValue) {
    state.leadType.quoteType = newValue;

    if (state.leadType.subsidyProduct !== null) {
      state.leadType.subsidyProduct = newValue;
    }
  },
  SET_TRUMP_FLAG(state, newValue) {
    state.leadType.trumpFlag = newValue;
  },
  SET_STATUS(state, newValue) {
    state.leadType.status = newValue;
  },
  SET_CAN_PAUSE(state, canPause) {
    state.leadType.canPause = canPause;
  },
  SET_CHANNEL_TYPE(state, newValue) {
    state.leadType.productTypeId = newValue;
  },
  SET_CHANNEL_NAME(state, newValue) {
    state.leadType.customName = newValue;
  },
  SET_MIN_PRICE(state, newValue) {
    state.leadType.minPayout = newValue;
  },
  SET_MAX_PRICE(state, newValue) {
    state.leadType.maxPayout = newValue;
  },
  SET_IS_HIDE_PHONE_NUMBER(state, newValue) {
    state.leadType.isHidePhoneNumber = newValue;
  },
  SET_CHANNEL_PRICE(state, newValue) {
    state.leadType.payout = newValue;
  },
  SET_POST_TEMPLATE(state, newValue) {
    state.leadType.postTemplate = newValue;
  },
  SET_FILTER_LIST(state, newValue) {
    state.leadType.filterList = newValue;
  },
  SET_SCHEDULE(state, newValue) {
    state.leadType.schedule = newValue;
  },
  SET_MAX_SOLD(state, newValue) {
    state.leadType.maxSold = newValue;
  },
  SET_PACKAGE_RESTRICTIONS(state, newValue) {
    state.packageRestrictions = newValue;
  },
  SET_SOURCE_PACKAGE(state, { sourcePackageId }) {
    if (!state.leadType.sourcePackage) {
      state.leadType.sourcePackage = {};
    }

    const sourcePackageUpdated = state.leadType.sourcePackage.id !== sourcePackageId;

    state.leadType.sourcePackage.id = sourcePackageId;

    if (sourcePackageId !== null && sourcePackageId !== 'NO_PACKAGE') {
      const startDate =
        state.leadType.sourcePackage.startDate && !sourcePackageUpdated
          ? moment(state.leadType.sourcePackage.startDate)
          : moment();

      state.leadType.sourcePackage.startDate = startDate.toDate();
    }
  },
  SET_QUOTE_TYPE_LOCK(state, newValue) {
    state.leadType.quoteTypeIsLocked = newValue;
  },
  SET_BACKFILL(state, newValue) {
    state.leadType.backfill = newValue;
  },
  SET_MISSEDCALLPENALTY(state, newValue) {
    state.leadType.missedCallPenalty = newValue;
  },
  SET_PREMIUM(state, newValue) {
    state.leadType.isPremium = newValue;
  },
  SET_APPLYANSWERRATE(state, newValue) {
    state.leadType.applyAnswerRate = newValue;
  },
  SET_ENABLERECORDING(state, newValue) {
    state.leadType.enableRecording = newValue;
  },
  SET_UNANSWEREDTHRESHOLD(state, newValue) {
    state.leadType.unansweredThreshold = newValue;
  },
  SET_ENABLEAUTOPAUSE(state, newValue) {
    state.leadType.enableAutoPause = newValue;
  },
  SET_CALL_TYPE(state, newValue) {
    state.leadType.callType = newValue;
  },
  SET_TERRITORIES(state, newValue) {
    state.leadType.geoMaps = newValue;
  },
  ADD_TERRITORY(state, newValue) {
    state.leadType.geoMaps.push(newValue);
  },
  ADD_TERRITORIES(state, geoMaps) {
    state.leadType.geoMaps.push(...geoMaps);
  },
  REMOVE_TERRITORY(state, index) {
    state.leadType.geoMaps.splice(index, 1);
  },
  REMOVE_TERRITORY_STATE(state, index) {
    const tempArray = state.leadType.geoMaps.filter(
      states => states.stateAbbreviation !== state.leadType.geoMaps[index].stateAbbreviation
    );

    state.leadType.geoMaps = tempArray;
  },
  REMOVE_TERRITORY_STATES(state, stateAbbrev) {
    const tempArray = state.leadType.geoMaps.filter(
      states => states.stateAbbreviation !== stateAbbrev
    );

    state.leadType.geoMaps = tempArray;
  },
  REMOVE_ZIPCODE(state, index) {
    state.leadType.geoMaps.splice(index, 1);
  },
  SET_CLIENT_ID(state, newValue) {
    state.leadType.clientId = newValue;
  },
  SET_MODIFIER(state, newValue) {
    state.leadType.modifier = newValue;
  },
  SET_BILLABLE_TIME(state, newValue) {
    state.leadType.billTimeThreshold = newValue;
  },
  SET_COMMERCIAL_NAME(state, newValue) {
    state.leadType.commercialName = newValue;
  },
  SET_EPR(state, date) {
    state.leadType.eprResetDate = date;
  },
  SET_CANCEL_SAVE_PACKAGES(state, packages) {
    state.cancelSavePackages = packages;
  },
  SET_PACKAGES(state, packages) {
    state.packages = packages;
  },
  ADD_PRICE_MODIFIER(state, priceModifier) {
    const foundPriceModifier = state.storedPriceModifiers.find(
      p => p.packagePriceModifierId === priceModifier.packagePriceModifierId
    );
    if (!foundPriceModifier) {
      state.storedPriceModifiers.push({ ...priceModifier });
    } else {
      foundPriceModifier.isSelected = true;
      foundPriceModifier.price = priceModifier.price;
    }
    const modifierToAdd = {
      id: null,
      packagePriceModifierId: priceModifier.packagePriceModifierId,
      clientQuoteTypeId: priceModifier.clientQuoteTypeId,
      price: priceModifier.price,
    };
    state.leadType.priceModifiers ??= [];
    state.leadType.priceModifiers.push(modifierToAdd);
  },
  UPDATE_PRICE_MODIFIER(state, priceModifier) {
    const foundPriceModifierIndex = state.storedPriceModifiers.findIndex(
      p => p.packagePriceModifierId === priceModifier.packagePriceModifierId
    );
    if (foundPriceModifierIndex > -1) {
      state.storedPriceModifiers[foundPriceModifierIndex] = {
        ...priceModifier,
      };
    } else {
      state.storedPriceModifiers.push({ ...priceModifier });
    }
    const modifierToUpdate = state.leadType.priceModifiers.find(
      modifierToFind =>
        modifierToFind.packagePriceModifierId === priceModifier.packagePriceModifierId
    );
    if (modifierToUpdate) {
      modifierToUpdate.price = priceModifier.price;
    } else {
      const modifierToAdd = {
        id: null,
        packagePriceModifierId: priceModifier.packagePriceModifierId,
        clientQuoteTypeId: priceModifier.clientQuoteTypeId,
        price: priceModifier.price,
      };
      state.leadType.priceModifiers ??= [];
      state.leadType.priceModifiers.push(modifierToAdd);
    }
  },
  REMOVE_PRICE_MODIFIER(state, priceModifier) {
    const foundPriceModifier = state.storedPriceModifiers.find(
      p => p.packagePriceModifierId === priceModifier.packagePriceModifierId
    );
    if (!foundPriceModifier) {
      state.storedPriceModifiers.push({ ...priceModifier });
    } else {
      foundPriceModifier.isSelected = false;
    }
    if (!state.leadType.priceModifiers) {
      return;
    }
    state.leadType.priceModifiers = state.leadType.priceModifiers.filter(
      filter => filter.packagePriceModifierId !== priceModifier.packagePriceModifierId
    );
  },
  CLEAR_PRICE_MODIFIERS(state) {
    if (!state.leadType.priceModifiers) {
      return;
    }
    state.leadType.priceModifiers = [];
  },
  SET_STORED_PRICE_MODIFIERS: (state, value) => {
    state.storedPriceModifiers = value;
  },
  SET_INACTIVE_PACKAGE(state, inactivePackage) {
    state.inactivePackage = inactivePackage;
  },
  SET_PACKAGE_BASE_PRICE(state, price) {
    state.packageBasePrice = price;
  },
  SET_SUBSCRIPTION_DESCRIPTION(state, des) {
    state.leadType.subscriptionDescription = des;
  },
  SET_ENABLE_RECORDING(state, newValue) {
    state.leadType.enableRecording = newValue;
  },
  SET_TEMPORARY_PAUSE(state, temporaryPauseModel) {
    state.leadType.temporaryPause = temporaryPauseModel;
  },
  SET_TEMPORARY_PAUSE_DURATION(state, duration) {
    state.temporaryPauseDuration = duration;
  },
  UPDATE_CUSTOM_PACKAGE_COST(state, cost) {
    const customPackage = state.packages.find(p => p.id === DEFAULT_PACKAGE_ID);
    customPackage.cost = cost;
    state.packages = [...state.packages.filter(p => p.id !== DEFAULT_PACKAGE_ID), customPackage];
  },
  SET_ORIGINAL_PACKAGE_ID(state, id) {
    state.originalPackageId = id;
  },
  SET_ORIGINAL_DAY_CAP_OVERRIDES(state, dayCapOverrides) {
    state.originalDayCapOverrides =
      dayCapOverrides !== null
        ? {
            ...dayCapOverrides,
          }
        : null;
  },
  SET_BID_FLOOR(state, bf) {
    state.leadType.bidFloor = bf;
  },
  SET_ACCOUNT_REPS(state, reps) {
    state.accountReps = reps;
  },
  SET_UPSELL_REP(state, rep) {
    state.leadType.upsellRep = rep;
  },
  SET_IS_LEG_ANCHOR(state, status) {
    state.leadType.isLegAnchor = status;
  },
  SET_UPSELL_PROMO(state, promo) {
    state.leadType.upsellPromo = promo;
  },
  SET_EPR_MULTIPLIER(state, value) {
    state.leadType.eprMultiplier = value;
  },
  SET_ESTIMATE(state, newValue) {
    state.leadType.estimateAmount = newValue;
  },
  SET_AVAILABLE_LUT_QUOTE_TYPES(state, value) {
    state.availableLutQuoteTypes = value;
  },
  SET_EXCLUDE_LIST(state, value) {
    // hacking for the moment.  This will need to take the string and convert it to an array

    state.leadType.secondaryTerritoryExcludedZipCodes = value;
  },
  SET_EXPANSION_OPTOUT(state, value) {
    state.leadType.expansionOptOut = value;
  },
  SET_STATE_SETTINGS_OVERRIDES(state, newValue) {
    state.stateSettingsOverrides = newValue;
  },
  SET_NEW_WEB_LEAD_SCHEDULE(state, scheduleToLeadTypeSchedule) {
    state.leadType.schedule = scheduleToLeadTypeSchedule;
  },
  SET_CUSTOM_PRICE_MODIFIERS(state, priceModifiers) {
    state.customPriceModifiers = priceModifiers;
  },
  SET_SUBSIDY_PRODUCT(state, subsidyProduct) {
    state.leadType.subsidyProduct = subsidyProduct;
  },
};

export const actions = {
  async addAllStateCounties({ commit, dispatch, state }, { stateAbbreviation, counties }) {
    const geoMaps = state.leadType.geoMaps.filter(
      g => g.type === 'County' && g.stateAbbreviation === stateAbbreviation
    );
    const countiesSet = [...new Set(geoMaps.map(t => t.county))];

    const toAdd = [];
    counties.forEach(x => {
      if (countiesSet.indexOf(x.county) === -1) {
        const newTerr = {
          county: x.countyName,
          stateAbbreviation: x.stateAbbr,
          type: 'County',
          zipCode: null,
        };

        toAdd.push(newTerr);
      }
    });

    commit('ADD_TERRITORIES', toAdd);

    // Remove the state all selected entry if it exists.
    dispatch('removeStateAll', stateAbbreviation);
  },
  async addAllCountyZips({ commit, dispatch, state }, { stateAbbreviation, countyName, zips }) {
    const geoMaps = state.leadType.geoMaps.filter(
      g =>
        g.type === 'ZipCode' && g.county === countyName && g.stateAbbreviation === stateAbbreviation
    );
    const zipSet = [...new Set(geoMaps.map(t => t.zipCode))];

    const toAdd = [];
    zips.forEach(x => {
      if (zipSet.indexOf(x.zipCode) === -1) {
        const newTerr = {
          county: x.countyName,
          stateAbbreviation,
          type: 'ZipCode',
          zipCode: x.zipCode,
        };

        toAdd.push(newTerr);
      }
    });

    commit('ADD_TERRITORIES', toAdd);

    // Remove the county all selected entry if it exists.
    dispatch('removeCountyAll', { stateAbbreviation, countyName });
  },
  async addState({ commit, state }, stateAbbreviation) {
    if (!stateAbbreviation) {
      return;
    }

    // Removes state, counties, and zip codes associated w/ state
    const newTerritories = state.leadType.geoMaps.filter(
      g => g.stateAbbreviation !== stateAbbreviation
    );

    // Add state
    newTerritories.push({
      county: null,
      stateAbbreviation,
      type: 'State',
      zipCode: null,
    });

    commit('SET_TERRITORIES', newTerritories);
  },
  async addCounty({ commit, state }, county) {
    if (!county) {
      return;
    }

    const { name, stateAbbr } = county;
    if (!name || !stateAbbr) {
      return;
    }

    // Removes counties and zip codes associated w/ county
    const newTerritories = state.leadType.geoMaps.filter(
      g => !(g.stateAbbreviation === stateAbbr && g.county === name)
    );

    // Add county
    newTerritories.push({
      county: name,
      stateAbbreviation: stateAbbr,
      type: 'County',
      zipCode: null,
    });

    commit('SET_TERRITORIES', newTerritories);
  },
  async addZipCode({ commit, state }, { stateAbbreviation, county, zipCode }) {
    // Remove zip code
    const newTerritories = state.leadType.geoMaps.filter(
      g =>
        !(g.stateAbbreviation === stateAbbreviation && g.county === county && g.zipCode === zipCode)
    );

    // Add zip
    newTerritories.push({
      county,
      stateAbbreviation,
      type: 'ZipCode',
      zipCode,
    });

    commit('SET_TERRITORIES', newTerritories);
  },
  async removeZipCode({ commit, state }, { stateAbbreviation, county, zipCode }) {
    // Remove zip code
    const newTerritories = state.leadType.geoMaps.filter(
      g =>
        !(g.stateAbbreviation === stateAbbreviation && g.county === county && g.zipCode === zipCode)
    );

    commit('SET_TERRITORIES', newTerritories);
  },
  async removeState({ commit, state }, stateAbbreviation) {
    // Removes state, counties, and zip codes associated w/ state
    const newTerritories = state.leadType.geoMaps.filter(
      g => g.stateAbbreviation !== stateAbbreviation
    );

    commit('SET_TERRITORIES', newTerritories);
  },
  async removeStateAll({ commit, state }, stateAbbreviation) {
    const stateEntries = state.leadType.geoMaps.filter(
      g => g.stateAbbreviation === stateAbbreviation && g.type === 'State'
    );
    if (stateEntries && stateEntries.length > 0) {
      const toRemove = stateEntries[0];
      commit('REMOVE_TERRITORY', toRemove);
    }
  },
  async removeCountyAll({ commit, state }, { stateAbbreviation, countyName }) {
    const countyEntries = state.leadType.geoMaps.filter(
      g =>
        g.stateAbbreviation === stateAbbreviation && g.county === countyName && g.type === 'County'
    );
    if (countyEntries && countyEntries.length > 0) {
      const toRemove = countyEntries[0];
      commit('REMOVE_TERRITORY', toRemove);
    }
  },
  async removeCounty({ commit, state }, { stateAbbreviation, countyName, zips }) {
    // Remove all zips for county and the county (all) entry.
    const existingGeos = state.leadType.geoMaps;

    for (const zip of zips) {
      const zipIndex = existingGeos.findIndex(
        existing => existing.type === 'ZipCode' && existing.zipCode === zip
      );

      if (zipIndex >= 0) {
        commit('REMOVE_TERRITORY', zipIndex);
        continue;
      }
    }
    // Remove county all
    const countyIndex = existingGeos.findIndex(
      existing =>
        existing.type === 'County' &&
        existing.county === countyName &&
        existing.stateAbbreviation === stateAbbreviation
    );

    if (countyIndex >= 0) {
      commit('REMOVE_TERRITORY', countyIndex);
    }
  },

  async removeTerritoryCounty({ commit, state }, { stateAbbreviation, countyName, zips }) {
    // Remove all zips for county and the county (all) entry.
    const existingGeos = state.leadType.geoMaps;

    for (const zip of zips) {
      const zipIndex = existingGeos.findIndex(
        existing => existing.type === 'ZipCode' && existing.zipCode === zip
      );

      if (zipIndex >= 0) {
        commit('REMOVE_TERRITORY', zipIndex);
        continue;
      }
    }
    // Remove county all
    const countyIndex = existingGeos.findIndex(
      existing =>
        existing.type === 'County' &&
        existing.county === countyName &&
        existing.stateAbbreviation === stateAbbreviation
    );

    if (countyIndex >= 0) {
      commit('REMOVE_TERRITORY', countyIndex);
    }
  },

  getPackageRestrictions({ commit, state }, updateMaxSold = true) {
    const packageId = state.leadType.packageId;
    return axiosInstance
      .get(`/api/LeadType/${packageId}/restrictions`)
      .then(response => {
        const data = response.data;
        commit('SET_PACKAGE_RESTRICTIONS', data);

        if (updateMaxSold) {
          commit('SET_MAX_SOLD', data.maxSold);
        }
        return data;
      })
      .catch(err => {
        console.error(err);
        return Promise.reject(err);
      });
  },

  async getLeadType({ commit }, clientQuoteTypeId) {
    commit('setLoading', true);

    const response = await axiosInstance.get(`/api/LeadType/${clientQuoteTypeId}`);

    const { temporaryPause } = response.data;

    if (temporaryPause) {
      const { start, scheduledEnd } = temporaryPause;
      const duration = Date.parse(scheduledEnd) - Date.parse(start);
      commit('SET_TEMPORARY_PAUSE_DURATION', duration);
    }

    response.data.isNew = false;
    commit('SET_LEAD_TYPE', response.data);
    commit('SET_ORIGINAL_PACKAGE_ID', response.data.packageId);
    commit('SET_ORIGINAL_DAY_CAP_OVERRIDES', response.data.dayCapOverrides);
    commit('SET_SUBSIDY_PRODUCT', response.data.subsidyProduct);

    if (response.data.stateOverrides?.length) {
      // Convert state overrides to state settings
      const stateSettings = response.data.stateOverrides.map(stateOverride => {
        return {
          stateAbbreviation: stateOverride.state,
          minBid: stateOverride.price.minBid,
          maxBid: stateOverride.price.maxBid,
          // effectiveValue: Not used yet?
          isActivated: stateOverride.isActive,
        };
      });

      commit('SET_STATE_SETTINGS_OVERRIDES', stateSettings);
    } else {
      // Reset state settings
      commit('SET_STATE_SETTINGS_OVERRIDES', []);
    }

    commit('setLoading', false);
    return response.data;
  },
  async getTerritoryDetails({ commit, state }, product) {
    const response = await axiosInstance.get(
      `/api/LeadType/territorydetail/${state.leadType.id}?subsidyProduct=${product}`
    );

    commit('SET_TERRITORY_DETAILS', response.data);
  },
  async clearLeadType({ commit }) {
    commit('CLEAR_LEAD_TYPE');
  },

  async getNewLeadType({ commit }, { serviceType, isAgent, isSignup, parentCompany, clientId }) {
    commit('setLoading', true);

    const response = await axiosInstance.get(
      `/api/LeadType/new/${serviceType}?isSignup=${isSignup}&clientId=${clientId}`
    );

    if (serviceType.toLowerCase() === 'call' && isAgent) {
      response.data.enableAutoPause = true;
    } else {
      response.data.enableAutoPause = false;
    }

    response.data.isNew = true;
    commit('SET_LEAD_TYPE', response.data);
    commit('SET_PACKAGE_ID', null);
    commit('setLoading', false);

    // A little bit of a nasty hack here.  Need to set subsidyProduct for SF Clients
    if (['STATEFARM'].includes(parentCompany)) {
      commit('SET_SUBSIDY_PRODUCT', response.data.quoteType);
    }

    return response.data;
  },

  async getCrmTypes({ commit }, productType) {
    const response = await axiosInstance.get(`/api/LeadType/crms/${productType}`).catch(err => err);

    if (response instanceof Error) {
      // TODO: Error handling
      console.error(response);
      return;
    }

    commit('SET_CRM_TYPES', response.data);
  },

  async verifyECRM({ rootState }, crm) {
    // Get email value in either io or existing client
    const email =
      rootState?.signup?.signupInfo?.client?.agencyEmail ||
      rootState?.client?.client?.agencyEmail ||
      '';

    if (!email) {
      showErrorToast('ECRM cannot be added because Agency Email for client is not set');
      return false;
    }

    // Get string leading up to @ symbol
    const [emailId] = /[^@]*/.exec(email);

    // verify that email is correct
    if (emailId?.toLowerCase() === crm.variables[0].toLowerCase()) {
      return true;
    } else {
      showErrorToast('Invalid ECRM Email');
      return false;
    }
  },

  async getPackage(_, { packageId }) {
    const getPackageUrl = `/api/LeadType/package/${packageId}`;

    try {
      const response = await axiosInstance.get(getPackageUrl);
      return response.data;
    } catch (err) {
      showErrorToast(err?.response?.data?.message ?? 'Failed to retrieve the package');
      return null;
    }
  },

  async getPackageList(
    { commit, dispatch, state },
    { productType, parentCompany, addCustom, isCorpClient }
  ) {
    let getPackageListUrl = `/api/LeadType/${parentCompany}/${state.leadType.quoteType}/${productType}`;

    if (state.leadType.serviceType === 'Call') {
      if (
        state.leadType != null &&
        state.leadType.originalPackageId != null &&
        state.leadType.originalPackageId.length > 0
      ) {
        getPackageListUrl = `/api/LeadType/calls/${parentCompany}/${state.leadType.id}`;
      } else if (state.leadType.callType === 'ColdTransfer') {
        getPackageListUrl = `/api/LeadType/calls/taz/${parentCompany}/${state.leadType.quoteType}`;
      } else if (parentCompany === 'STATEFARM') {
        getPackageListUrl = `/api/LeadType/calls/taz/${parentCompany}/${state.leadType.quoteType}`;
      } else {
        getPackageListUrl = `/api/LeadType/calls/quotetype/${parentCompany}/${state.leadType.quoteType}`;
      }
    }

    const response = await dispatch('request/get', {
      url: getPackageListUrl,
      errorMessage: 'Failed to retrieve packages.',
    });

    if (!response) return;

    const packages = response.data;

    let packageBasePrice = 0.0;
    // corp types should have custom package set to 0 price as default
    if (!isCorpClient) {
      switch (state.leadType.quoteType.toLowerCase()) {
        case 'home':
          packageBasePrice = 15.0;
          break;
        case 'renter':
          packageBasePrice = 9.0;
          break;
        case 'health':
          packageBasePrice = 8.0;
          break;
        case 'medsupp':
          packageBasePrice = 12.0;
          break;
        default:
          packageBasePrice = 11.25;
      }
    }

    commit('SET_PACKAGE_BASE_PRICE', packageBasePrice);

    if (addCustom) {
      let filterList = [];

      // Okay this is an adventure.   When you load an existing custom lead type
      // the price can get stomped on.  I'm trying to force it to use the existing one.
      if (state.leadType.packageId === DEFAULT_PACKAGE_ID) {
        filterList = state.leadType.filterList;
      }

      packages.push({
        id: DEFAULT_PACKAGE_ID,
        filters: filterList,
        name: 'Custom',
        cost: state.leadType.payout,
        serviceType: 'LEADS',
      });
    }

    // if package is inactive
    if (state.leadType.packageId) {
      const currentPackageId = state.leadType?.packageId?.toUpperCase();
      if (currentPackageId && !packages.some(x => x.id.toUpperCase() === currentPackageId)) {
        const inactivePackage = await dispatch('getPackage', {
          packageId: currentPackageId,
        });
        commit('SET_INACTIVE_PACKAGE', inactivePackage);
        if (state.inactivePackage) {
          packages.splice(0, 0, inactivePackage);
        }
      }
    }

    // Populate Price Modifiers if they exist on the package & fill out missing info
    // map what is on the channel itself to price modifiers. This will override/set the isSelected and the saved Price
    const channelPackage = packages.find(
      chanPackage => chanPackage?.id?.toUpperCase() === state?.leadType?.packageId?.toUpperCase()
    );
    if (state.leadType.packageId) {
      channelPackage.priceModifiers ??= [];
      channelPackage.priceModifiers.forEach(channelPriceModifier => {
        if (state.leadType.priceModifiers?.length > 0) {
          const foundModifier = state.leadType.priceModifiers.find(
            foundChannelModifier =>
              foundChannelModifier.packagePriceModifierId ===
              channelPriceModifier.packagePriceModifierId
          );
          if (foundModifier) {
            // override price to match what is saved and it's existence indicates selection.
            channelPriceModifier.price = foundModifier.price;
            channelPriceModifier.isSelected = true;
            commit('SET_STORED_PRICE_MODIFIERS', [
              ...state.storedPriceModifiers,
              channelPriceModifier,
            ]);
          }
        }
      });
    }
    // Sort price modifiers by Name so we have consistent display
    packages.forEach(packageProduct => {
      packageProduct.priceModifiers?.sort((a, b) => {
        const aName = a?.name ?? '';
        const bName = b?.name ?? '';
        return aName.localeCompare(bName);
      });
    });
    commit('SET_PACKAGES', packages);
  },

  updateCustomPackageFilters({ commit, state }, filters) {
    const customPackage = {
      ...(state.packages.find(p => p.id === DEFAULT_PACKAGE_ID) || {}),
      filters,
    };

    const packages = state.packages.filter(p => p.id !== DEFAULT_PACKAGE_ID);
    packages.push(customPackage);

    commit('SET_PACKAGES', packages);
  },

  updatePostTemplate({ commit, state }) {
    const prefix = 'EMAIL:';
    let postTemplateString = prefix;

    if (state.leadType.emailDelivery.length > 0) {
      postTemplateString += state.leadType.emailDelivery.join(',');
    }

    if (state.leadType.crmDelivery.length > 0) {
      if (postTemplateString.length > prefix.length) {
        postTemplateString += ',';
      }

      for (const v of state.leadType.crmDelivery) {
        const workingAddress = v.variables.join('|');
        postTemplateString += workingAddress + '@' + v.domain + ',';
      }
    }

    commit('SET_POST_TEMPLATE', postTemplateString);
  },

  async createLeadType({
    commit,
    dispatch,
    rootState,
    state,
    getters: { selectedStates, isCampaign },
    rootGetters,
  }) {
    commit('setLoading', true);
    let leadTypeSchedule = [];

    if (state.leadType.serviceType.toLowerCase() === 'web') {
      leadTypeSchedule = await dispatch('scheduler/saveWebLeadSchedules', DEFAULT_PACKAGE_ID, {
        root: true,
      });
    }

    const leadType = {
      ...state.leadType,
      id: DEFAULT_PACKAGE_ID,
      clientId: rootState.client.client.id,
      stateOverrides: campaignService.mapStateSettingsToOverrides(
        state.stateSettingsOverrides,
        selectedStates
      ),
      schedule: leadTypeSchedule,
    };

    const result = await (isCampaign
      ? createCampaign(rootGetters, rootState, leadType)
      : createChannel(dispatch, leadType));

    commit('setLoading', false);

    return result;
  },

  async getCustomPriceModifiers({ commit, dispatch, state }) {
    const response = await dispatch('request/get', {
      url: '/api/leadtype/price-modifiers',
      errorMessage: 'Failed to get price modifiers for custom package.',
    });

    if (!response) return;

    const priceModifieres = response.data;
    commit('SET_CUSTOM_PRICE_MODIFIERS', priceModifieres);

    priceModifieres.forEach(channelPriceModifier => {
      if (state.leadType.priceModifiers?.length > 0) {
        const foundModifier = state.leadType.priceModifiers.find(
          foundChannelModifier =>
            foundChannelModifier.packagePriceModifierId ===
            channelPriceModifier.packagePriceModifierId
        );
        if (foundModifier) {
          // override price to match what is saved and it's existence indicates selection.
          channelPriceModifier.price = foundModifier.price;
          channelPriceModifier.isSelected = true;
          commit('SET_STORED_PRICE_MODIFIERS', [
            ...state.storedPriceModifiers,
            channelPriceModifier,
          ]);
        }
      }
    });
  },

  async selectPackage(
    { commit, dispatch, rootGetters, state },
    { packageId, isCustomPackage, isCorpClient }
  ) {
    // Early exit if package is already selected
    if (state.leadType.packageId === packageId) {
      return;
    }

    commit('SET_PACKAGE_ID', packageId);
    const selectedPackage = state.packages.find(p => p.id === packageId);

    // if corp type and custom package reset price to 0
    let price = 0;
    if (isCorpClient && isCustomPackage) {
      price = 0;
    } else {
      price = selectedPackage.cost;
    }

    commit('SET_CHANNEL_PRICE', price);

    commit('SET_MAX_PRICE', 0);
    dispatch('getPackageRestrictions');
    commit('CLEAR_PRICE_MODIFIERS');

    // Update subsidyProduct for StateFarm clients when package changes
    if (rootGetters['client/isStateFarm']) {
      try {
        await dispatch('updateSubsidyProduct', {
          packageId,
          isCustomPackage,
        });
      } catch (error) {
        console.error('Failed to update subsidyProduct:', error);
      }
    }

    // Add price modifiers that are available for the selected package if they
    // were previously selected.
    const priceModifiers = isCustomPackage
      ? state.customPriceModifiers
      : selectedPackage.priceModifiers;

    priceModifiers?.forEach(availablePriceModifier => {
      const storedPriceModifier = state.storedPriceModifiers?.find(
        spm => spm.packagePriceModifierId === availablePriceModifier.packagePriceModifierId
      );
      // Re-select price modifier if it was previously stored as selected.
      if (storedPriceModifier?.isSelected) {
        commit('ADD_PRICE_MODIFIER', {
          ...storedPriceModifier,
          price: isCustomPackage
            ? storedPriceModifier.price
            : lodash.clamp(
                storedPriceModifier.price,
                availablePriceModifier.priceMin,
                availablePriceModifier.priceMax
              ),
        });
        return;
      }

      // Select price modifier if it isn't stored yet and is default selected.
      if (availablePriceModifier.isDefaultSelected && !storedPriceModifier) {
        commit('ADD_PRICE_MODIFIER', {
          ...availablePriceModifier,
          isSelected: true,
        });
      }
    });
  },

  async updateLeadType({
    commit,
    dispatch,
    state,
    getters: { selectedStates, isCampaign },
    rootState,
    rootGetters,
  }) {
    try {
      commit('setLoading', true);

      if (state.leadType.serviceType.toLowerCase() === 'web') {
        const schedule = await dispatch('scheduler/saveWebLeadSchedules', state.leadType.id, {
          root: true,
        });

        if (!schedule) {
          return false;
        }

        commit('SET_NEW_WEB_LEAD_SCHEDULE', schedule);
      }

      const leadType = {
        ...state.leadType,
        stateOverrides: campaignService.mapStateSettingsToOverrides(
          state.stateSettingsOverrides,
          selectedStates
        ),
      };

      return await (isCampaign
        ? updateCampaign(rootGetters, rootState, leadType)
        : updateChannel(dispatch, leadType));
    } finally {
      commit('setLoading', false);
    }
  },

  async updateSubsidyProduct(
    { commit, dispatch, rootState, state },
    { packageId, isCustomPackage }
  ) {
    try {
      // For custom packages, use the quote type as subsidyProduct
      if (isCustomPackage) {
        commit('SET_SUBSIDY_PRODUCT', state.leadType.quoteType);
        // Update existing territories with new subsidy information
        await dispatch('updateExistingTerritoriesSubsidy');
        return;
      }

      // For standard packages, get subsidyProduct from API
      const response = await axiosInstance.get(
        `/api/LeadType/subsidyProduct?parentCompany=${rootState.client.client.parentCompany}&packageId=${packageId}&isCall=${state.leadType.serviceType === 'Call'}&quoteType=${state.leadType.quoteType}`
      );

      if (response.data) {
        commit('SET_SUBSIDY_PRODUCT', response.data);
        // Update existing territories with new subsidy information
        await dispatch('updateExistingTerritoriesSubsidy');
      }
    } catch (error) {
      // Fallback to quote type if API call fails
      commit('SET_SUBSIDY_PRODUCT', state.leadType.quoteType);
      // Update existing territories with new subsidy information
      await dispatch('updateExistingTerritoriesSubsidy');
    }
  },

  async updateExistingTerritoriesSubsidy({ commit, rootState, state }) {
    try {
      // Only update for StateFarm clients
      if (rootState.client.client?.parentCompany !== 'STATEFARM') {
        return;
      }

      // Only update if we have territories and a subsidyProduct
      if (!state.leadType.geoMaps?.length || !state.leadType.subsidyProduct) {
        return;
      }

      // Refresh territory details with new subsidyProduct
      if (state.leadType.id) {
        const response = await axiosInstance.get(
          `/api/LeadType/territorydetail/${state.leadType.id}?subsidyProduct=${state.leadType.subsidyProduct}`
        );

        if (response.data) {
          commit('SET_TERRITORY_DETAILS', response.data);
        }
      }
    } catch (error) {
      console.error('Error updating existing territories subsidy:', error);
    }
  },

  async resetEPR({ commit, rootState }, clientQuoteTypeId) {
    const response = await axiosInstance
      .post(`/api/LeadType/${rootState.client.client.id}/${clientQuoteTypeId}/resetepr`)
      .catch(err => err);

    if (response instanceof Error) {
      const message = response?.response?.data?.message ?? 'Failed to reset EPR.';
      showErrorToast(message);
    }

    commit('SET_EPR', new Date().toISOString());
  },

  async getCancelSavePackages({ commit, rootState, state }) {
    if (rootState.client.client) {
      const response = await axiosInstance
        .get(
          `/api/LeadType/${rootState.client.client.parentCompany}/${state.leadType.quoteType}/sourceOnlyPackages`
        )
        .catch(err => err);

      if (response instanceof Error) {
        if (response.response.status === 401) {
          // 401 means user should not see these.
          // This is an expected response code.
          return;
        }
        const message =
          (response.response && response.response.data.message) ||
          'Could not retrieve source packages';
        showErrorToast(message);
      }

      commit('SET_CANCEL_SAVE_PACKAGES', response.data);
    }
  },

  async temporaryPauseLeadType({ commit, state }) {
    commit('setLoading', true);

    let durationMs = state.temporaryPauseDuration;

    if (state.temporaryPauseDuration === -1) {
      durationMs = moment().startOf('day').add(1, 'day').diff(moment());
    }

    const clientQuoteTypeId = state.leadType.id;
    const duration = moment().startOf('day').add(durationMs, 'ms').format('HH:mm:ss');

    const response = await axiosInstance
      .post('/api/LeadType/pause', {
        clientQuoteTypeId,
        duration,
      })
      .catch(err => err);

    if (response instanceof Error) {
      errorTracker.trackException({
        exception: response,
      });
      console.error(response);
      showErrorToast('Failed to pause channel.');
      commit('setLoading', false);
      return;
    }

    commit('SET_TEMPORARY_PAUSE', response.data);
    commit('SET_STATUS', 'PAUSE');
    commit('setLoading', false);
  },

  async temporaryUnpauseLeadType({ commit, state }) {
    commit('setLoading', true);

    const response = await axiosInstance
      .delete(`/api/LeadType/pause/${state.leadType.id}`)
      .catch(err => err);

    if (response instanceof Error) {
      errorTracker.trackException({
        exception: response,
      });
      console.error(response);
      showErrorToast('Failed to unpause channel.');
      commit('setLoading', false);
      return;
    }

    commit('SET_TEMPORARY_PAUSE', null);
    commit('SET_TEMPORARY_PAUSE_DURATION', null);
    commit('SET_STATUS', 'ACTIVE');
    commit('setLoading', false);
  },

  async getClientGeoMapChanges(_, { clientQuoteTypeId, changeDate }) {
    const response = await axiosInstance.get(
      `/api/leadType/${clientQuoteTypeId}/clientgeomapchanges`,
      {
        params: {
          changeDate,
        },
      }
    );
    const data = response.data;

    return data;
  },

  async removeGeoMaps({ commit, state }, geoMaps) {
    for (const geo of geoMaps) {
      const existingGeos = state.leadType.geoMaps;

      const zipIndex = existingGeos.findIndex(
        existing => existing.type === 'ZipCode' && existing.zipCode === geo.zipCode
      );

      if (zipIndex >= 0) {
        commit('REMOVE_TERRITORY', zipIndex);
        continue;
      }

      const stateIndex = existingGeos.findIndex(
        existing =>
          existing.type === 'State' && existing.stateAbbreviation === geo.stateAbbreviation
      );

      if (stateIndex >= 0) {
        const response = await axiosInstance
          .delete('/api/territory/states/remove-zip', {
            data: geo,
          })
          .catch(err => err);

        commit('REMOVE_TERRITORY', stateIndex);
        commit('ADD_TERRITORIES', response.data);
      }
    }
  },

  async getQuoteTypeFilterChanges(_, { clientQuoteTypeId, changeDate }) {
    const response = await axiosInstance.get(
      `/api/leadType/${clientQuoteTypeId}/getclientquotetypefilterchanges`,
      {
        params: {
          changeDate,
        },
      }
    );
    const data = response.data;

    return data;
  },

  async cloneFrom({ commit, dispatch }, { clientQuoteTypeId, type }) {
    commit('setLoading', true);

    const response = await axiosInstance
      .get(`/api/LeadType/${clientQuoteTypeId}`)
      .catch(err => err);

    if (response instanceof Error) {
      errorTracker.trackException({
        exception: response,
      });
      console.error(response);
      commit('setLoading', false);
      showErrorToast('Failed to clone from lead type.');
      return;
    }

    const { geoMaps, dayCapSchedule, dayCapOverrides, crmDelivery, emailDelivery, phoneNumbers } =
      response.data;

    switch (type) {
      case 'TERRITORY':
        commit('SET_TERRITORIES', geoMaps);
        break;
      case 'DELIVERY':
        if (crmDelivery) {
          commit('SET_CRM_DELIVERY', crmDelivery);
          dispatch('updatePostTemplate');
        }
        if (emailDelivery) {
          commit('SET_EMAIL_DELIVERY', emailDelivery);
          dispatch('updatePostTemplate');
        }
        if (phoneNumbers) {
          commit('SET_PHONE_DELIVERY', phoneNumbers);
        }
        break;
      case 'CAPS':
        commit('SET_DAY_CAPS', {
          dayCapSchedule,
          dayCapOverrides,
          clonedChannel: clientQuoteTypeId,
        });
        break;
      default:
        throw new Error('Invalid cloneFrom TYPE');
    }

    commit('setLoading', false);
  },

  resetState({ commit, dispatch }) {
    dispatch('scheduler/resetSchedulerState', null, {
      root: true,
    });
    commit('RESET_STATE');
  },

  updateLeadTypeScheduleForNewChannels({ commit, state }, schedule) {
    if (state.leadType.schedule === null) {
      commit('SET_NEW_WEB_LEAD_SCHEDULE', schedule);
    }
  },

  async loadAccountRepOptions({ commit }) {
    const response = await request.get('/api/Clients/ACCOUNT_REPS/options', {
      errorMessage: 'Failed to load account representatives.',
      loadingMutation: 'leadType/setLoading',
    });

    if (response) {
      commit('SET_ACCOUNT_REPS', response.data);
    }
  },

  setLutInfo({ commit }, lutGeoMaps) {
    commit(
      'SET_ESTIMATE',
      lutGeoMaps.reduce((sum, geo) => (sum += geo.estimate), 0)
    );
    commit('SET_TERRITORIES', convertLutGeoMapsToGeoMaps(lutGeoMaps));
  },

  async getAvailableLutQuoteTypes({ commit, dispatch }, parentCompany) {
    const response = await dispatch('request/get', {
      url: `/api/leadtype/quoteTypes/${parentCompany}`,
      loadingMutation: 'leadType/setLoading',
      errorMessage: false,
      onError: error => {
        console.error(error);
      },
    });

    if (!response) {
      return;
    }

    commit('SET_AVAILABLE_LUT_QUOTE_TYPES', response.data);
  },

  async setTerritories({ commit }, geoMaps) {
    commit('SET_TERRITORIES', geoMaps);
  },

  async setExcludedZipCodes({ commit }, zipCodes) {
    commit('SET_EXCLUDE_LIST', zipCodes);
  },

  async setExpansionOptOut({ commit }, flag) {
    commit('SET_EXPANSION_OPTOUT', flag);
  },

  setWeekdayCaps({ state, commit }, newWeekdayCap) {
    const days = Object.keys(state.leadType.dayCapSchedule);

    for (const day of days) {
      if (day !== 'weekly') {
        const value = ['saturday', 'sunday'].includes(day) ? 1 : newWeekdayCap;

        commit('SET_DAY_CAP', {
          day,
          value,
        });
      }
    }
  },

  async bulkUpdateOptInSecondaryTerritories(_, { form, optIn }) {
    const response = await request.post(
      `/api/leadType/bulk-update-secondary-territories-opt-status?optIn=${optIn}`,
      form,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        errorMessage: 'Failed to update secondary territories opt-in status.',
      }
    );

    return response?.data ?? null;
  },

  setDayCaps({ rootGetters, state, commit }, dayCapSchedule) {
    const currentDayCapSchedule = state.leadType.dayCapSchedule;
    if (currentDayCapSchedule.weekly !== dayCapSchedule.weekly && dayCapSchedule.weekly !== -1) {
      calcDailyCapsFromWeeklyCap(dayCapSchedule);
    }
    let dayCapOverrides = null;
    if (
      state.originalDayCapOverrides !== null &&
      currentDayCapSchedule.weekly !== dayCapSchedule.weekly &&
      dayCapSchedule.weekly !== -1
    ) {
      dayCapOverrides = calcOverridesForSchedule(
        dayCapSchedule,
        rootGetters['scheduler/schedules'],
        !rootGetters['scheduler/preventCapsPerScheduleOverride']
      );
    }
    commit('SET_DAY_CAPS', {
      dayCapSchedule,
      dayCapOverrides,
      clonedChannel: null,
    });
  },

  setDelivery({ commit }, { emailDelivery, crmDelivery, phoneDelivery }) {
    commit('SET_EMAIL_DELIVERY', emailDelivery);
    commit('SET_CRM_DELIVERY', crmDelivery);
    commit('SET_PHONE_DELIVERY', phoneDelivery);
  },

  setLeadType({ commit }, leadType) {
    commit('SET_LEAD_TYPE', leadType);
  },

  setMaxSold({ commit }, maxSold) {
    commit('SET_MAX_SOLD', maxSold);
  },
};
